import React, { useMemo } from 'react';
import ManCrossPageFooter from '../components/ManCrossPageFooter';

// Color de celda según tipo del driver (Spec S21)
// LIDERAZGO → royal (blue), AUTOCUIDADO → teal, CULTURA → mint
const getTypeColor = (tipo = '') => {
    const t = String(tipo).toUpperCase();
    if (t.includes('LIDERAZGO'))    return '#30388e';  // royal blue
    if (t.includes('AUTOCUIDADO'))  return '#00b7a3';  // teal
    if (t.includes('CULTURA'))      return '#18af4b';  // mint
    return '#9AA2AE';
};

const ManCrossPageDriversImpact = ({ reportData, headerText, pageNumber }) => {
    // El backend calcula los drivers (Spec §1.4 + §6.3) — el frontend solo renderiza
    const payload = reportData?.payload || {};
    const drivers = payload.drivers || [];
    console.log('Drivers Impact Data:', drivers);
    
    const totalDeltaICP = useMemo(
        () => drivers.reduce((sum, d) => sum + (Number(d.delta_ICP) || 0), 0),
        [drivers]
    );

    const calloutText = useMemo(() => {
        if (drivers.length === 0) {
            return 'Todas las dimensiones están en Riesgo Bajo. El enfoque ahora es sostener la excelencia.';
        }
        const n = drivers.length;
        const icp = Math.round(totalDeltaICP * 10) / 10;
        return `Intervenir estos ${n} drivers aumenta el ICP en +${icp} puntos en los próximos 90 días`;
    }, [drivers, totalDeltaICP]);

    const footerText = useMemo(() => {
        if (drivers.length === 0) {
            return 'Sin drivers de intervención: la organización está protegida contra co-fallos de barreras. Mantener.';
        }
        const inmediatos = drivers.filter(d => String(d.plazo).toLowerCase().includes('inmediata'));
        const preventivos = drivers.filter(d => !String(d.plazo).toLowerCase().includes('inmediata'));
        const parteInm = inmediatos.length > 0
            ? `Los drivers 1 y 2 de intervención inmediata eliminan el IVC Nivel 3 en 30 días.`
            : '';
        const partePrev = preventivos.length > 0
            ? ` Los drivers 3 y 4 eliminan el Nivel 2 en 60 días.`
            : '';
        return (parteInm + partePrev).trim() || 'Los drivers de impacto reducen la co-falla activa y mejoran el ICP en 90 días.';
    }, [drivers]);

    return (
        <div className="mgcr-drivers-page">
            <div className="mgcr-page-header">
                <span className="mgcr-page-header-text">{headerText}</span>
                <span className="mgcr-page-header-num">{pageNumber}</span>
            </div>

            <div className="keep-blankspace" />

            <div className="mgcr-dx-title-area">
                <h2 className="mgcr-dx-main-title">Drivers de Impacto</h2>
                <p className="mgcr-dx-subtitle">Proyección a 90 días</p>
            </div>

            {drivers.length === 0 ? (
                // Caso vacío: todas las dimensiones en verde (Riesgo Bajo)
                <div className="mgcr-drivers-empty-state">
                    <div className="mgcr-drivers-empty-icon">✓</div>
                    <div className="mgcr-drivers-empty-title">Estado Óptimo Alcanzado</div>
                    <div className="mgcr-drivers-empty-text">
                        {calloutText}
                    </div>
                    <div className="mgcr-drivers-empty-subtitle">
                        Mantener la disciplina y continuar con el monitoreo periódico.
                    </div>
                </div>
            ) : (
                // Caso normal: hay drivers que intervenir
                <>
                    <div className="mgcr-drivers-callout">
                        {calloutText}
                    </div>

                    <div className="mgcr-drivers-table">
                        <div className="mgcr-drivers-head">
                            <span>Tipo</span>
                            <span>Driver</span>
                            <span>Score → Meta</span>
                            <span>ICP+</span>
                            <span>IVC</span>
                            <span>Plazo</span>
                            <span>Resp.</span>
                        </div>

                        {drivers.map((row, idx) => {
                            const orden = row.orden || (idx + 1);
                            const rowColor = getTypeColor(row.tipo);
                            const scoreActual = Number.isFinite(Number(row.score_actual)) ? Number(row.score_actual) : null;
                            const scoreMeta = Number.isFinite(Number(row.score_meta)) ? Number(row.score_meta) : null;
                            const progress = scoreActual != null && scoreMeta != null && scoreMeta > 0
                                ? Math.max(0, Math.min(1, scoreActual / scoreMeta))
                                : 0;
                            const scoreText = scoreActual == null
                                ? `— → ${scoreMeta ?? '—'}`
                                : `${Math.round(scoreActual)} → ${Math.round(scoreMeta ?? 0)}`;
                            const deltaICP = Number.isFinite(Number(row.delta_ICP)) ? Number(row.delta_ICP) : 0;

                    return (
                        <div key={orden} className="mgcr-drivers-row">
                            <div className="mgcr-drivers-tipo-cell" style={{ background: rowColor }}>
                                <div className="mgcr-drivers-tipo-num">
                                    {String(orden).padStart(2, '0')}
                                </div>
                                <div className="mgcr-drivers-tipo-label">
                                    {String(row.tipo || '').toUpperCase()}
                                </div>
                            </div>

                            <div className="mgcr-drivers-driver-cell">
                                <div className="mgcr-drivers-driver-title">{row.driver}</div>
                            </div>

                            <div className="mgcr-drivers-score-cell">
                                <div className="mgcr-drivers-score-text" style={{ color: rowColor }}>
                                    {scoreText}
                                </div>
                                <div className="mgcr-drivers-score-track">
                                    <div
                                        className="mgcr-drivers-score-fill"
                                        style={{ width: `${Math.round(progress * 100)}%`, background: rowColor }}
                                    />
                                </div>
                            </div>

                            <div className="mgcr-drivers-mini-card">
                                <span className="mgcr-drivers-mini-card-label">ICP</span>
                                <span className="mgcr-drivers-mini-card-main">+{deltaICP.toFixed(1)} pts</span>
                            </div>

                            <div className="mgcr-drivers-mini-card">
                                <span className="mgcr-drivers-mini-card-label">IVC</span>
                                <span className="mgcr-drivers-mini-card-main">{row.impacto_IVC}</span>
                                <span className="mgcr-drivers-mini-card-sub" style={{ color: rowColor }}>
                                    {row.plazo}
                                </span>
                            </div>

                            <div className="mgcr-drivers-time-cell">{row.plazo}</div>
                            <div className="mgcr-drivers-resp-cell">{row.responsable}</div>
                        </div>
                    );
                        })}
                    </div>

                    <div className="mgcr-insight-footer">
                        <strong>Esto implica que:</strong> {footerText}
                    </div>
                </>
            )}

            <div className="keep-blankspace" />
            <ManCrossPageFooter />
        </div>
    );
};

export default ManCrossPageDriversImpact;